import type { RenterProfileApiData, RenterProfileApiQuery } from '@/src/lib/api/endpoints/user';

type BooleanString = 'true' | 'false';

export type RenterProfileFormData = {
  moveInDate?: string;
  moveInTimeFrame?: string;
  leaseDuration?: number | null;
  incomeYearly?: number;
  numBeds?: number;
  numOccupants?: number;
  desiredPets?: string;
  creditScore?: string;
  creditScoreMin?: number;
  creditScoreMax?: number;
  desiredParking?: string;
};

function padZero(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function toBooleanString(value?: string): BooleanString {
  return value === 'true' ? 'true' : 'false';
}

export function transformRenterProfileFromApi(data: RenterProfileApiData = {}): RenterProfileFormData {
  const result: RenterProfileFormData = {};

  if (data.moveInDate && Array.isArray(data.moveInDate)) {
    const [year, month, day] = data.moveInDate;
    result.moveInDate = `${year}-${padZero(month)}-${padZero(day)}`;
  }

  result.moveInTimeFrame = data.moveInTimeFrame ? data.moveInTimeFrame.toLowerCase() : '';

  const leaseLength = data.leaseLengthMonths || data.leaseDuration;
  result.leaseDuration = leaseLength ? Number(leaseLength) : null;

  result.incomeYearly = data.incomeYearly;

  const numBedrooms =
    data.numBedrooms !== undefined && !isNaN(data.numBedrooms) && data.numBedrooms >= 0
      ? data.numBedrooms
      : data.numBeds;
  result.numBeds = numBedrooms;

  result.numOccupants = data.numOccupants;

  // Pet detection: handles legacy petDetails and renterPetDetails payloads.
  const petDetails = data.petDetails;
  const renterPetDetails = data.renterPetDetails;
  if (data.desiredPets !== undefined) {
    result.desiredPets = toBooleanString(data.desiredPets);
  } else if (
    (petDetails && petDetails.length >= 1 && petDetails[0]?.type !== 'NONE') ||
    (renterPetDetails && renterPetDetails.petType && renterPetDetails.petType !== 'NONE')
  ) {
    result.desiredPets = 'true';
  } else {
    result.desiredPets = 'false';
  }

  if (data.creditScoreMin && data.creditScoreMax) {
    result.creditScore = `${data.creditScoreMin}-${data.creditScoreMax}`;
    result.creditScoreMin = data.creditScoreMin;
    result.creditScoreMax = data.creditScoreMax;
  }

  if (data.desiredParking !== undefined) {
    result.desiredParking = data.desiredParking === 'covered' ? 'true' : toBooleanString(data.desiredParking);
  }

  return result;
}

type RenterProfileToApiInput = {
  moveInDate?: string;
  moveInTimeFrame?: string;
  leaseDuration?: number | string;
  numOccupants?: number | string;
  incomeYearly?: number | string;
  creditScoreMin?: number | string;
  creditScoreMax?: number | string;
  numBeds?: number | string;
  desiredPets?: string;
  desiredParking?: string;
};

export function transformRenterProfileToApi(data: RenterProfileToApiInput = {}): RenterProfileApiQuery {
  const query: RenterProfileApiQuery = {};

  if (data.moveInDate) {
    query.moveInDate = data.moveInDate;
  }

  if (data.moveInTimeFrame) {
    query.moveInTimeFrame = data.moveInTimeFrame;
  }

  if (data.leaseDuration) {
    query.leaseLengthMonths = Number(data.leaseDuration);
  }

  if (data.numOccupants) {
    query.numOccupants = Number(data.numOccupants);
  }

  if (data.incomeYearly) {
    query.incomeYearly = Number(data.incomeYearly);
  }

  if (data.creditScoreMin) {
    query.creditScoreMin = Number(data.creditScoreMin);
  }

  if (data.creditScoreMax) {
    query.creditScoreMax = Number(data.creditScoreMax);
  }

  if (data.numBeds !== undefined && !isNaN(Number(data.numBeds)) && Number(data.numBeds) >= 0) {
    query.numBedrooms = Number(data.numBeds);
  }

  if (data.desiredPets !== undefined) {
    query.desiredPets = data.desiredPets;
  }

  if (data.desiredParking !== undefined) {
    query.desiredParking = data.desiredParking;
  }

  return query;
}
